// ProfileManagement.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGlobe,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaCalendar,
  FaIdCard,
  FaFileAlt,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaPlus,
  FaEdit,
  FaSave,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useArenaAuth } from "../../context/ArenaAuthContext";
import {
  validatePassword,
  validatePasswordMatch,
} from "../../utils/passwordValidation";

// Leaflet icon setup
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const center = [33.6844, 73.0479]; // Islamabad, Pakistan coordinates

function SetViewOnClick({ coords, shouldPan }) {
  const map = useMap();
  useEffect(() => {
    if (shouldPan && coords[0] !== 0 && coords[1] !== 0) {
      map.setView(coords, 13);
    }
  }, [coords, map, shouldPan]);
  return null;
}

const ProfileManagement = () => {
  const [profile, setProfile] = useState({
    name: "",
    date_established: "",
    email: "",
    address: {
      country: "",
      city: "",
      area: "",
      street: "",
      plot_no: "",
      coordinates: "",
    },
    phone: "",
    contact_persons: [
      {
        first_name: "",
        middle_name: "",
        last_name: "",
        phone_no: "",
        email: "",
      },
    ],
    website: "",
    business_registration_no: "",
    tax_no: "",
    other_license: "",
    licenses: [{ title: "", reference_number: "" }],
    about_arena: "",
    facebook: "",
    twitter: "",
    instagram: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [sportsOptions, setSportsOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  // Map state
  const [mapCenter, setMapCenter] = useState(center);
  const [marker, setMarker] = useState(null);
  const [shouldPan, setShouldPan] = useState(false);
  const [mapError, setMapError] = useState("");
  const mapRef = useRef(null);
  const { arenaUser } = useArenaAuth();

  const fetchArenaInformation = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/get-arenaInformation/${arenaUser?.arenaId}`,
        { credentials: "include" }
      );
      const data = await response.json();
      const arenaData = data;

      // Parse address data
      let addressData = {
        country: "",
        city: "",
        area: "",
        street: "",
        plot_no: "",
        coordinates: "",
      };

      if (arenaData.address_details) {
        addressData = {
          country: arenaData.address_details.country || "",
          city: arenaData.address_details.city || "",
          area: arenaData.address_details.area || "",
          street: arenaData.address_details.street || "",
          plot_no: arenaData.address_details.plot_no || "",
          coordinates: arenaData.address_details.coordinates || "",
        };

        // Set map position if coordinates exist
        if (addressData.coordinates) {
          const coords = addressData.coordinates
            .split(",")
            .map((c) => parseFloat(c.trim()));
          if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
            setMapCenter(coords);
            setMarker(coords);
          }
        }
      }

      setProfile({
        name: arenaData.Arena_name || "",
        date_established: arenaData.date_established
          ? new Date(arenaData.date_established).toISOString().split("T")[0]
          : "",
        email: arenaData.email || "",
        address: addressData,
        phone: arenaData.telephone || "",
        contact_persons: [
          {
            first_name: arenaData.Contact_details.Fname,
            middle_name: arenaData.Contact_details.Mname,
            last_name: arenaData.Contact_details.Lname,
            phone_no: arenaData.Contact_details.phone,
            email: arenaData.Contact_details.email || "",
          },
        ],
        website: arenaData.website || "",
        business_registration_no: arenaData.business_registration_number || "",
        tax_no: arenaData.tax_payer_number || "",
        other_license: arenaData.other_license || "",
        licenses:
          arenaData.licenses && arenaData.licenses.length > 0
            ? arenaData.licenses
            : [{ title: "", reference_number: "" }],
        about_arena: arenaData.about_arena || "",
        facebook: arenaData.facebook_url || "",
        twitter: arenaData.x_url || "",
        instagram: arenaData.instagram_url || "",
      });

      const sportsResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/get-sportsOptions/${arenaUser?.arenaId}`,
        { credentials: "include" }
      );
      const sportsData = await sportsResponse.json();

      setSportsOptions(sportsData);
    } catch (error) {
      console.error("Error fetching arena information:", error);
    }
  };

  // Validation functions
  const validateRequiredField = (value, fieldName) => {
    if (!value || value.trim() === "") {
      return `${fieldName} is required`;
    }
    return "";
  };

  const validateEmail = (email) => {
    if (!email) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return "Phone number is required";
    // Accept both mobile (03XXX-XXXXXXX) and landline (0XX-XXXXXXX) formats
    const mobileRegex = /^0\d{3}-\d{7}$/; // 03XXX-XXXXXXX
    const landlineRegex = /^0\d{2,3}-\d{7}$/; // 0XX-XXXXXXX
    if (!mobileRegex.test(phone) && !landlineRegex.test(phone)) {
      return "Phone number must be in format 03XXX-XXXXXXX or 0XX-XXXXXXX";
    }
    return "";
  };

  const validateAtLeastOneContactPerson = (contactPersons) => {
    if (!contactPersons || contactPersons.length === 0) {
      return "At least one contact person is required";
    }

    const hasValidContact = contactPersons.some(
      (person) =>
        person.first_name.trim() !== "" &&
        person.last_name.trim() !== "" &&
        person.phone_no.trim() !== ""
    );

    return hasValidContact
      ? ""
      : "At least one contact person must have first name, last name, and phone number (format: 03XXX-XXXXXXX or 0XX-XXXXXXX)";
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    newErrors.name = validateRequiredField(profile.name, "Arena name");
    newErrors.email = validateEmail(profile.email);
    newErrors.date_established = validateRequiredField(
      profile.date_established,
      "Date of establishment"
    );
    newErrors.about_arena = validateRequiredField(
      profile.about_arena,
      "About arena"
    );

    // Address validations (house number is optional)
    newErrors.city = validateRequiredField(profile.address.city, "City");
    newErrors.area = validateRequiredField(profile.address.area, "Area");
    newErrors.street = validateRequiredField(profile.address.street, "Street");
    // House number is optional - no validation needed

    // Phone validation
    newErrors.phone = validatePhoneNumber(profile.phone);

    // Contact person validation
    newErrors.contactPersons = validateAtLeastOneContactPerson(
      profile.contact_persons
    );

    // Validate contact persons
    for (let i = 0; i < profile.contact_persons.length; i++) {
      const person = profile.contact_persons[i];
      if (!person.first_name.trim()) {
        newErrors[`contactPerson_${i}_firstName`] = `Contact person ${
          i + 1
        }: First name is required`;
      }
      if (!person.last_name.trim()) {
        newErrors[`contactPerson_${i}_lastName`] = `Contact person ${
          i + 1
        }: Last name is required`;
      }
      if (person.email && !/\S+@\S+\.\S+/.test(person.email)) {
        newErrors[`contactPerson_${i}_email`] = `Contact person ${
          i + 1
        }: Valid email format required`;
      }
      if (!person.phone_no.trim()) {
        newErrors[`contactPerson_${i}_phone`] = `Contact person ${
          i + 1
        }: Phone number is required`;
      } else {
        const phoneError = validatePhoneNumber(person.phone_no);
        if (phoneError) {
          newErrors[`contactPerson_${i}_phone`] = `Contact person ${
            i + 1
          }: ${phoneError}`;
        }
      }
    }

    setErrors(newErrors);

    // Check if there are any validation errors
    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) {
      toast.error("Please fix the errors in the form before submitting.");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (arenaUser?.arenaId) fetchArenaInformation();
  }, [arenaUser?.arenaId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile((prevProfile) => ({
      ...prevProfile,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleContactPersonChange = (index, field, value) => {
    const newContactPersons = [...profile.contact_persons];
    newContactPersons[index][field] = value;
    setProfile({ ...profile, contact_persons: newContactPersons });
  };

  const addContactPerson = () => {
    setProfile({
      ...profile,
      contact_persons: [
        ...profile.contact_persons,
        {
          first_name: "",
          middle_name: "",
          last_name: "",
          phone_no: "",
          email: "",
        },
      ],
    });
  };

  const removeContactPerson = (index) => {
    if (profile.contact_persons.length > 1) {
      const newContactPersons = profile.contact_persons.filter(
        (_, i) => i !== index
      );
      setProfile({ ...profile, contact_persons: newContactPersons });
    }
  };

  // License handling functions
  const handleLicenseChange = (index, field, value) => {
    const newLicenses = [...profile.licenses];
    newLicenses[index][field] = value;
    setProfile({ ...profile, licenses: newLicenses });
  };

  const addLicense = () => {
    setProfile({
      ...profile,
      licenses: [...profile.licenses, { title: "", reference_number: "" }],
    });
  };

  const removeLicense = (index) => {
    if (profile.licenses.length > 1) {
      const newLicenses = profile.licenses.filter((_, i) => i !== index);
      setProfile({ ...profile, licenses: newLicenses });
    }
  };

  // Address/Map handling functions
  const handleAddressChange = (field, value) => {
    setProfile({
      ...profile,
      address: {
        ...profile.address,
        [field]: value,
      },
    });
  };

  // Password change functions
  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validatePasswordChange = () => {
    // Validate current password
    if (!passwordData.oldPassword.trim()) {
      toast.error("Please enter your current password");
      return false;
    }

    // Validate new password
    const newPasswordError = validatePassword(passwordData.newPassword);
    if (newPasswordError) {
      toast.error(newPasswordError);
      return false;
    }

    // Validate password confirmation
    const confirmPasswordError = validatePasswordMatch(
      passwordData.newPassword,
      passwordData.confirmPassword
    );
    if (confirmPasswordError) {
      toast.error(confirmPasswordError);
      return false;
    }

    return true;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validatePasswordChange()) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/change-arena-password/${arenaUser?.arenaId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            oldPassword: passwordData.oldPassword,
            newPassword: passwordData.newPassword,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        toast.success("Password changed successfully!");
        setPasswordData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setIsChangingPassword(false);
        setShowPasswords({
          old: false,
          new: false,
          confirm: false,
        });
      } else {
        toast.error(result.message || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("Error changing password. Please try again.");
    }
  };

  const geocodeAddress = useCallback(async () => {
    const { country, city, area, street, plot_no } = profile.address;
    const address = `${plot_no} ${street}, ${area}, ${city}, ${country}`.trim();

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search`,
        {
          params: {
            q: address,
            format: "json",
            limit: 1,
            addressdetails: 1,
          },
        }
      );

      if (response.data.length > 0) {
        const { lat, lon, address: details } = response.data[0];
        setMapCenter([parseFloat(lat), parseFloat(lon)]);
        setMarker([parseFloat(lat), parseFloat(lon)]);
        setShouldPan(true);

        setProfile((prevProfile) => ({
          ...prevProfile,
          address: {
            ...prevProfile.address,
            country: details.country || prevProfile.address.country,
            city: details.city || details.town || prevProfile.address.city,
            area: details.suburb || prevProfile.address.area,
            street: details.road || prevProfile.address.street,
            plot_no: details.house_number || prevProfile.address.plot_no,
            coordinates: `${lat}, ${lon}`,
          },
        }));
        setMapError("");
      } else {
        setMapError("No location found for this address");
        toast.error("No location found for this address");
      }
    } catch (error) {
      console.error("Error geocoding:", error);
      setMapError("Error finding location");
      toast.error("Error finding location");
    }
  }, [profile.address]);

  const handleMapClick = useCallback(async (e) => {
    const { lat, lng } = e.latlng;
    setMarker([lat, lng]);
    setMapCenter([lat, lng]);
    setShouldPan(true);

    setProfile((prevProfile) => ({
      ...prevProfile,
      address: {
        ...prevProfile.address,
        coordinates: `${lat}, ${lng}`,
      },
    }));

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse`,
        {
          params: {
            lat: lat,
            lon: lng,
            format: "json",
            addressdetails: 1,
          },
        }
      );

      if (response.data) {
        const { address } = response.data;
        setProfile((prevProfile) => ({
          ...prevProfile,
          address: {
            ...prevProfile.address,
            country: address.country || "",
            city: address.city || address.town || "",
            area:
              address.suburb || address.neighbourhood || address.county || "",
            street: address.road || "",
            plot_no: address.house_number || "",
            coordinates: `${lat}, ${lng}`,
          },
        }));
        setMapError("");
      } else {
        setMapError("No address found for this location");
        toast.error("No address found for this location");
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setMapError("Error fetching address information");
      toast.error("Error fetching address information");
    }
  }, []);

  const MapEvents = () => {
    useMapEvents({
      click: handleMapClick,
      dragstart: () => setShouldPan(false),
      zoomstart: () => setShouldPan(false),
    });
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/update-arenaInformation/${arenaUser?.arenaId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            name: profile.name,
            date_established: profile.date_established,
            email: profile.email,
            address: profile.address,
            telephone: profile.phone,
            website: profile.website,
            business_registration_number: profile.business_registration_no,
            tax_payer_number: profile.tax_no,
            other_license: profile.other_license,
            licenses: profile.licenses.filter(
              (license) => license.title.trim() !== ""
            ),
            about_arena: profile.about_arena,
            facebook_url: profile.facebook,
            x_url: profile.twitter,
            instagram_url: profile.instagram,
            contact_persons: profile.contact_persons,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update arena information");
      }

      const result = await response.json();
      console.log("Profile updated:", result);

      if (response.ok) {
        toast.success("Profile updated successfully!");
        fetchArenaInformation();
        setIsEditing(false);
      } else {
        toast.error(result.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Error updating profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
          <FaUser className="text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
          <span className="truncate">Profile Management</span>
        </h2>
        <p className="text-gray-600 text-xs sm:text-sm md:text-base">
          Manage your arena profile information and settings
        </p>
      </div>

      {/* Password Change Section */}
      <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 mb-4 sm:mb-6 md:mb-8">
        <button
          onClick={() => setIsChangingPassword(!isChangingPassword)}
          className="bg-gray-600 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm md:text-base hover:bg-gray-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
        >
          <FaLock className="text-xs sm:text-sm" />
          {isChangingPassword ? "Cancel" : "Change Password"}
        </button>

        {isChangingPassword && (
          <form
            onSubmit={handlePasswordSubmit}
            className="mt-4 sm:mt-6 bg-gradient-to-r from-gray-50 to-blue-50 p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl border border-gray-200"
          >
            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
              Change Password
            </h3>
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                  <FaLock className="text-gray-500 text-xs sm:text-sm" />
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.old ? "text" : "password"}
                    value={passwordData.oldPassword}
                    onChange={(e) =>
                      handlePasswordChange("oldPassword", e.target.value)
                    }
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("old")}
                    className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPasswords.old ? <FaEyeSlash className="text-sm sm:text-base" /> : <FaEye className="text-sm sm:text-base" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                  <FaLock className="text-green-500 text-xs sm:text-sm" />
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      handlePasswordChange("newPassword", e.target.value)
                    }
                    minLength="6"
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("new")}
                    className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPasswords.new ? <FaEyeSlash className="text-sm sm:text-base" /> : <FaEye className="text-sm sm:text-base" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                  <FaLock className="text-blue-500 text-xs sm:text-sm" />
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      handlePasswordChange("confirmPassword", e.target.value)
                    }
                    minLength="6"
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("confirm")}
                    className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPasswords.confirm ? <FaEyeSlash className="text-sm sm:text-base" /> : <FaEye className="text-sm sm:text-base" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-4 sm:mt-6">
              <button
                type="submit"
                className="bg-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm md:text-base hover:bg-green-700 transition-all duration-300 flex items-center justify-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
              >
                <FaSave className="text-xs sm:text-sm" />
                Change Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordData({
                    oldPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                }}
                className="bg-gray-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm md:text-base hover:bg-gray-600 transition-all duration-300 flex items-center justify-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
              >
                <FaTimes className="text-xs sm:text-sm" />
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {isEditing ? (
        <div className="bg-white p-3 sm:p-4 md:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200">
          <div className="mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
              <FaEdit className="text-[#0071bc] text-sm sm:text-base" />
              Edit Profile Information
            </h3>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label
                htmlFor="name"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaUser className="text-[#0071bc] text-xs sm:text-sm" />
                Arena Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={profile.name}
                onChange={(e) => {
                  handleChange(e);
                  if (errors.name) {
                    setErrors((prev) => ({ ...prev, name: "" }));
                  }
                }}
                className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.name && (
                <div className="text-red-500 text-xs sm:text-sm mt-1">{errors.name}</div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label
                  htmlFor="date_established"
                  className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
                >
                  <FaCalendar className="text-green-500 text-xs sm:text-sm" />
                  <span className="truncate">Date of Establishment</span> <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date_established"
                  name="date_established"
                  value={profile.date_established}
                  onChange={(e) => {
                    handleChange(e);
                    if (errors.date_established) {
                      setErrors((prev) => ({ ...prev, date_established: "" }));
                    }
                  }}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                    errors.date_established
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  required
                />
                {errors.date_established && (
                  <div className="text-red-500 text-xs sm:text-sm mt-1">
                    {errors.date_established}
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
                >
                  <FaEnvelope className="text-blue-500 text-xs sm:text-sm" />
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profile.email}
                  onChange={(e) => {
                    handleChange(e);
                    if (errors.email) {
                      setErrors((prev) => ({ ...prev, email: "" }));
                    }
                  }}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                />
                {errors.email && (
                  <div className="text-red-500 text-xs sm:text-sm mt-1">
                    {errors.email}
                  </div>
                )}
              </div>
            </div>

            {/* Address Section */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl border border-gray-200">
              <h4 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-1 sm:gap-2">
                <FaMapMarkerAlt className="text-red-500 text-xs sm:text-sm md:text-base" /> Address Information
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <div>
                  <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter country"
                    value={profile.address.country}
                    onChange={(e) => {
                      handleAddressChange("country", e.target.value);
                      if (errors.country) {
                        setErrors((prev) => ({ ...prev, country: "" }));
                      }
                    }}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                      errors.country ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.country && (
                    <div className="text-red-500 text-xs sm:text-sm mt-1">
                      {errors.country}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter city"
                    value={profile.address.city}
                    onChange={(e) => {
                      handleAddressChange("city", e.target.value);
                      if (errors.city) {
                        setErrors((prev) => ({ ...prev, city: "" }));
                      }
                    }}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                      errors.city ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.city && (
                    <div className="text-red-500 text-xs sm:text-sm mt-1">
                      {errors.city}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                    Area <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter area"
                    value={profile.address.area}
                    onChange={(e) => {
                      handleAddressChange("area", e.target.value);
                      if (errors.area) {
                        setErrors((prev) => ({ ...prev, area: "" }));
                      }
                    }}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                      errors.area ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.area && (
                    <div className="text-red-500 text-xs sm:text-sm mt-1">
                      {errors.area}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                    Street <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter street"
                    value={profile.address.street}
                    onChange={(e) => {
                      handleAddressChange("street", e.target.value);
                      if (errors.street) {
                        setErrors((prev) => ({ ...prev, street: "" }));
                      }
                    }}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                      errors.street ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.street && (
                    <div className="text-red-500 text-xs sm:text-sm mt-1">
                      {errors.street}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                    Plot No{" "}
                    <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter plot number"
                    value={profile.address.plot_no}
                    onChange={(e) =>
                      handleAddressChange("plot_no", e.target.value)
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                    Coordinates
                  </label>
                  <input
                    type="text"
                    placeholder="Click on map to set coordinates"
                    value={profile.address.coordinates}
                    readOnly
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl bg-gray-100 cursor-not-allowed"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={geocodeAddress}
                className="mt-3 sm:mt-4 bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm md:text-base hover:bg-blue-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
              >
                <FaMapMarkerAlt className="text-xs sm:text-sm" />
                Find Coordinates
              </button>

              {mapError && (
                <p className="text-red-500 text-xs sm:text-sm mt-2">{mapError}</p>
              )}

              <div className="mt-3 sm:mt-4 rounded-lg sm:rounded-xl overflow-hidden border border-gray-300">
                <MapContainer
                  center={mapCenter}
                  zoom={13}
                  style={{ height: "200px", width: "100%" }}
                  className="sm:!h-[250px] md:!h-[300px]"
                  ref={mapRef}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {marker && <Marker position={marker} />}
                  <SetViewOnClick coords={mapCenter} shouldPan={shouldPan} />
                  <MapEvents />
                </MapContainer>
              </div>
            </div>

            <div>
              <label
                htmlFor="phone"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaPhone className="text-green-500 text-xs sm:text-sm" /> Phone Number{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={profile.phone}
                onChange={(e) => {
                  handleChange(e);
                  if (errors.phone) {
                    setErrors((prev) => ({ ...prev, phone: "" }));
                  }
                }}
                placeholder="03XXX-XXXXXXX or 0XX-XXXXXXX"
                className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                  errors.phone ? "border-red-500" : "border-gray-300"
                }`}
                pattern="[0-9\s\-\+\(\)]+"
                required
              />
              {errors.phone && (
                <div className="text-red-500 text-xs sm:text-sm mt-1">{errors.phone}</div>
              )}
            </div>

            <div>
              <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                Contact Persons <span className="text-red-500">*</span>
              </label>
              {errors.contactPersons && (
                <div className="text-red-500 text-xs sm:text-sm mt-1 mb-2">
                  {errors.contactPersons}
                </div>
              )}
              {profile.contact_persons.map((person, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200 mb-3 sm:mb-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter first name"
                        value={person.first_name}
                        onChange={(e) => {
                          handleContactPersonChange(
                            index,
                            "first_name",
                            e.target.value
                          );
                          if (errors[`contactPerson_${index}_firstName`]) {
                            setErrors((prev) => ({
                              ...prev,
                              [`contactPerson_${index}_firstName`]: "",
                            }));
                          }
                        }}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                          errors[`contactPerson_${index}_firstName`]
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        required
                      />
                      {errors[`contactPerson_${index}_firstName`] && (
                        <div className="text-red-500 text-xs sm:text-sm mt-1">
                          {errors[`contactPerson_${index}_firstName`]}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                        Middle Name{" "}
                        <span className="text-gray-500 text-xs">
                          (Optional)
                        </span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter middle name"
                        value={person.middle_name}
                        onChange={(e) =>
                          handleContactPersonChange(
                            index,
                            "middle_name",
                            e.target.value
                          )
                        }
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
                      />
                    </div>
                    <div className="sm:col-span-2 md:col-span-1">
                      <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter last name"
                        value={person.last_name}
                        onChange={(e) => {
                          handleContactPersonChange(
                            index,
                            "last_name",
                            e.target.value
                          );
                          if (errors[`contactPerson_${index}_lastName`]) {
                            setErrors((prev) => ({
                              ...prev,
                              [`contactPerson_${index}_lastName`]: "",
                            }));
                          }
                        }}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                          errors[`contactPerson_${index}_lastName`]
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        required
                      />
                      {errors[`contactPerson_${index}_lastName`] && (
                        <div className="text-red-500 text-xs sm:text-sm mt-1">
                          {errors[`contactPerson_${index}_lastName`]}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        placeholder="03XXX-XXXXXXX or 0XX-XXXXXXX"
                        value={person.phone_no}
                        onChange={(e) => {
                          handleContactPersonChange(
                            index,
                            "phone_no",
                            e.target.value
                          );
                          if (errors[`contactPerson_${index}_phone`]) {
                            setErrors((prev) => ({
                              ...prev,
                              [`contactPerson_${index}_phone`]: "",
                            }));
                          }
                        }}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                          errors[`contactPerson_${index}_phone`]
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        pattern="[0-9\s\-\+\(\)]+"
                        required
                      />
                      {errors[`contactPerson_${index}_phone`] && (
                        <div className="text-red-500 text-xs sm:text-sm mt-1">
                          {errors[`contactPerson_${index}_phone`]}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                        Email{" "}
                        <span className="text-gray-500 text-xs">
                          (Optional)
                        </span>
                      </label>
                      <input
                        type="email"
                        placeholder="Enter email"
                        value={person.email}
                        onChange={(e) => {
                          handleContactPersonChange(
                            index,
                            "email",
                            e.target.value
                          );
                          if (errors[`contactPerson_${index}_email`]) {
                            setErrors((prev) => ({
                              ...prev,
                              [`contactPerson_${index}_email`]: "",
                            }));
                          }
                        }}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                          errors[`contactPerson_${index}_email`]
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {errors[`contactPerson_${index}_email`] && (
                        <div className="text-red-500 text-xs sm:text-sm mt-1">
                          {errors[`contactPerson_${index}_email`]}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-2 mt-3 sm:mt-4">
                    {index === profile.contact_persons.length - 1 && (
                      <button
                        type="button"
                        onClick={addContactPerson}
                        className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm hover:bg-green-700 transition-all duration-300 flex items-center justify-center gap-1 sm:gap-2 shadow-md hover:shadow-lg"
                        title="Add Contact Person"
                      >
                        <FaPlus className="text-xs sm:text-sm" /> Add
                      </button>
                    )}
                    {profile.contact_persons.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeContactPerson(index)}
                        className="bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm hover:bg-red-700 transition-all duration-300 flex items-center justify-center gap-1 sm:gap-2 shadow-md hover:shadow-lg"
                        title="Remove Contact Person"
                      >
                        <FaTrash className="text-xs sm:text-sm" /> Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label
                htmlFor="website"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaGlobe className="text-blue-500 text-xs sm:text-sm" /> Website{" "}
                <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={profile.website}
                onChange={handleChange}
                placeholder="https://example.com"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>

            <div>
              <label
                htmlFor="business_registration_no"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaIdCard className="text-purple-500 text-xs sm:text-sm" /> <span className="truncate">Business Registration Number</span>{" "}
                <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                id="business_registration_no"
                name="business_registration_no"
                value={profile.business_registration_no}
                onChange={handleChange}
                placeholder="Enter business registration number"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>

            <div>
              <label
                htmlFor="tax_no"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaIdCard className="text-orange-500 text-xs sm:text-sm" /> Tax Number{" "}
                <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                id="tax_no"
                name="tax_no"
                value={profile.tax_no}
                onChange={handleChange}
                placeholder="Enter tax number"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>

            <div>
              <label
                htmlFor="other_license"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaFileAlt className="text-gray-500 text-xs sm:text-sm" /> Other License (Legacy){" "}
                <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                id="other_license"
                name="other_license"
                value={profile.other_license}
                onChange={handleChange}
                placeholder="Enter other license information"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>

            {/* Enhanced Licenses Section */}
            <div>
              <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
                <FaFileAlt className="text-teal-500 text-xs sm:text-sm" /> Licenses{" "}
                <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              {profile.licenses.map((license, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200 mb-2 sm:mb-3"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <input
                      type="text"
                      placeholder="License Title"
                      value={license.title}
                      onChange={(e) =>
                        handleLicenseChange(index, "title", e.target.value)
                      }
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
                    />
                    <input
                      type="text"
                      placeholder="Reference Number"
                      value={license.reference_number}
                      onChange={(e) =>
                        handleLicenseChange(
                          index,
                          "reference_number",
                          e.target.value
                        )
                      }
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-2 mt-2 sm:mt-3">
                    {index === profile.licenses.length - 1 && (
                      <button
                        type="button"
                        onClick={addLicense}
                        className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm hover:bg-green-700 transition-all duration-300 flex items-center justify-center gap-1 sm:gap-2 shadow-md hover:shadow-lg"
                        title="Add License"
                      >
                        <FaPlus className="text-xs sm:text-sm" /> Add
                      </button>
                    )}
                    {profile.licenses.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLicense(index)}
                        className="bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm hover:bg-red-700 transition-all duration-300 flex items-center justify-center gap-1 sm:gap-2 shadow-md hover:shadow-lg"
                        title="Remove License"
                      >
                        <FaTrash className="text-xs sm:text-sm" /> Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label
                htmlFor="about_arena"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaFileAlt className="text-indigo-500 text-xs sm:text-sm" /> About Arena{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                id="about_arena"
                name="about_arena"
                value={profile.about_arena}
                onChange={(e) => {
                  handleChange(e);
                  if (errors.about_arena) {
                    setErrors((prev) => ({ ...prev, about_arena: "" }));
                  }
                }}
                className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${
                  errors.about_arena ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Describe your arena, facilities, and services"
                rows="4"
                required
              />
              {errors.about_arena && (
                <div className="text-red-500 text-xs sm:text-sm mt-1">
                  {errors.about_arena}
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="facebook"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaFacebook className="text-blue-600 text-xs sm:text-sm" /> Facebook{" "}
                <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="url"
                id="facebook"
                name="facebook"
                value={profile.facebook}
                onChange={handleChange}
                placeholder="https://facebook.com/yourpage"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>

            <div>
              <label
                htmlFor="twitter"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaTwitter className="text-blue-400 text-xs sm:text-sm" /> Twitter{" "}
                <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="url"
                id="twitter"
                name="twitter"
                value={profile.twitter}
                onChange={handleChange}
                placeholder="https://twitter.com/yourhandle"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>

            <div>
              <label
                htmlFor="instagram"
                className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2"
              >
                <FaInstagram className="text-pink-600 text-xs sm:text-sm" /> Instagram{" "}
                <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="url"
                id="instagram"
                name="instagram"
                value={profile.instagram}
                onChange={handleChange}
                placeholder="https://instagram.com/yourhandle"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-4 sm:mt-6">
              <button
                type="submit"
                className="bg-green-600 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm md:text-base hover:bg-green-700 transition-all duration-300 flex items-center justify-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin text-xs sm:text-sm" />
                    SAVING...
                  </>
                ) : (
                  <>
                    <FaSave className="text-xs sm:text-sm" />
                    Save Changes
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="bg-gray-500 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm md:text-base hover:bg-gray-600 transition-all duration-300 flex items-center justify-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
                disabled={isSubmitting}
              >
                <FaTimes className="text-xs sm:text-sm" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white p-3 sm:p-4 md:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200">
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaUser className="text-[#0071bc] mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Arena Name:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-words">{profile.name}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaUser className="text-green-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Sports Facilities:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-words">{sportsOptions}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaCalendar className="text-blue-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Date Established:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base">{profile.date_established}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaEnvelope className="text-red-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Email:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-all">{profile.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaMapMarkerAlt className="text-purple-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Address:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-words">
                  {profile.address.plot_no && ` ${profile.address.plot_no}`}
                  {profile.address.street && ` ${profile.address.street},`}
                  {profile.address.area && ` ${profile.address.area},`}
                  {profile.address.city && ` ${profile.address.city},`}
                  {profile.address.country && ` ${profile.address.country}`}
                  {profile.address.coordinates && (
                    <span className="text-gray-600 text-xs block mt-1 break-all">
                      Coordinates: {profile.address.coordinates}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaPhone className="text-green-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Phone Number:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base">{profile.phone}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaUser className="text-indigo-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Contact Persons:</strong>
                <ul className="mt-1 sm:mt-2 space-y-1 sm:space-y-2">
                  {profile.contact_persons.map((person, index) => (
                    <li
                      key={index}
                      className="text-gray-900 text-xs sm:text-sm bg-gray-50 p-2 sm:p-3 rounded-lg break-words"
                    >
                      {person.first_name} {person.middle_name}{" "}
                      {person.last_name} - {person.phone_no}
                      {person.email && <span className="block sm:inline"> ({person.email})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaGlobe className="text-blue-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Website:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-all">
                  {profile.website || "Not provided"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaIdCard className="text-purple-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">
                  Business Registration Number:
                </strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-words">
                  {profile.business_registration_no || "Not provided"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaIdCard className="text-orange-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Tax Number:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-words">
                  {profile.tax_no || "Not provided"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaFileAlt className="text-gray-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Other License:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-words">
                  {profile.other_license || "None"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaFileAlt className="text-teal-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Licenses:</strong>
                <ul className="mt-1 sm:mt-2 space-y-1 sm:space-y-2">
                  {profile.licenses &&
                  profile.licenses.length > 0 &&
                  profile.licenses.some((license) => license.title) ? (
                    profile.licenses.map(
                      (license, index) =>
                        license.title && (
                          <li
                            key={index}
                            className="text-gray-900 text-xs sm:text-sm bg-gray-50 p-2 sm:p-3 rounded-lg break-words"
                          >
                            <strong>{license.title}</strong>
                            {license.reference_number &&
                              ` (Ref: ${license.reference_number})`}
                          </li>
                        )
                    )
                  ) : (
                    <li className="text-gray-600 italic text-xs sm:text-sm">
                      No licenses registered
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaFileAlt className="text-indigo-500 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">About Arena:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-words">
                  {profile.about_arena || "No information available"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaFacebook className="text-blue-600 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Facebook:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-all">
                  {profile.facebook || "Not provided"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
              <FaTwitter className="text-blue-400 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Twitter:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-all">
                  {profile.twitter || "Not provided"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4">
              <FaInstagram className="text-pink-600 mt-0.5 sm:mt-1 text-xs sm:text-sm flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <strong className="text-gray-700 text-xs sm:text-sm">Instagram:</strong>
                <p className="text-gray-900 mt-0.5 sm:mt-1 text-sm sm:text-base break-all">
                  {profile.instagram || "Not provided"}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setIsEditing(true)}
            className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm md:text-base hover:bg-blue-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl mt-4 sm:mt-6"
          >
            <FaEdit className="text-xs sm:text-sm" />
            Edit Profile
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileManagement;
